import { useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useCartStore } from '@/stores/cartStore';
import { CartItemCard } from '../checkout/CartItemCard';
import { formatPrice } from '@/utils/shopFormatters';
import { ShoppingCart, Store } from 'lucide-react';
import type { CartItem } from '@/types/cart';
import { useAuth } from '@/contexts';
import { useAddToCartModal } from '@/contexts/AddToCartModalContext';
import { ModalTriggerSource } from '@/types/cart';
import { transformProductToVariants } from '@/utils/cartHelpers';
import { productService } from '@/services/product/productService';
import { trackEvent } from '@/utils/analytics';

interface SellerGroup {
  sellerId: string;
  sellerName: string;
  items: CartItem[];
  subtotal: number;
}

export function CartDrawer() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openModal } = useAddToCartModal();

  const { items, totalItems, totalPrice, isCartOpen, closeCart, updateCartItem, removeFromCart } = useCartStore();

  useEffect(() => {
    if (user) {
      trackEvent('cart_viewed', {
        userId: user?._id,
        email: user?.email,
        name: user?.firstName + ' ' + user?.lastName,
        username: user?.username,
        country: user?.country,
        isActive: user?.isActive,
        isEmailVerified: user?.emailVerified,
        isPhoneVerified: user?.phoneVerified,
        isDisabled: user?.isDisabled,
        isNewBuyer: user?.isNewBuyer,
        isIdentityVerified: user?.isIdentityVerified,
        cartTotalItems: totalItems,
        cartTotalPrice: totalPrice,
      });
    }
  }, [user]);

  // Group items by seller
  const sellerGroups = useMemo(() => {
    const grouped = items.reduce((acc, item) => {
      const existingGroup = acc.find(g => g.sellerId === item.sellerId);
      
      if (existingGroup) {
        existingGroup.items.push(item);
        existingGroup.subtotal += item.totalPrice;
      } else {
        acc.push({
          sellerId: item.sellerId,
          sellerName: item.sellerName,
          items: [item],
          subtotal: item.totalPrice,
        });
      }
      
      return acc;
    }, [] as SellerGroup[]);

    return grouped;
  }, [items]);

  const handleCheckout = (sellerId: string) => {
    if (!user) {
      // Save the seller ID and redirect to login
      sessionStorage.setItem('checkoutSellerId', sellerId);
      closeCart();
      navigate('/login', { 
        state: { 
          from: { pathname: '/checkout' },
          message: 'Please login to complete your purchase'
        }
      });
      return;
    }
    // Store the selected seller ID in sessionStorage to access in checkout
    sessionStorage.setItem('checkoutSellerId', sellerId);
    closeCart();
    navigate('/checkout');
  };

  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    updateCartItem(itemId, { quantity: newQuantity });
  };

  const handleEditVariants = async (itemId: string) => {

    const cartItem = items.find(item => item.id === itemId);
    if (!cartItem) return;

    try {

      const product = await productService.getProduct(cartItem.productId);
      

      const productWithVariants = transformProductToVariants(product as any);
      

      openModal(productWithVariants, ModalTriggerSource.CART, cartItem);
      
    
    } catch (error) {
      console.error('Failed to fetch product for editing:', error);
    }
  };

  return (
    <Sheet open={isCartOpen} onOpenChange={(open) => !open && closeCart()}>
      <SheetContent className="w-full sm:max-w-[440px] p-0 flex flex-col h-full">
        <SheetHeader className="px-6 py-4 border-b border-black/5 flex-shrink-0">
          <SheetTitle className="font-lexend text-xl font-medium tracking-[-0.22px] capitalize text-black/80 flex items-center gap-2">
            <ShoppingCart size={24} />
            Shopping Cart ({totalItems})
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center overflow-y-auto">
            <ShoppingCart size={64} className="text-gray-300 mb-4" />
            <h3 className="font-lexend text-lg font-medium text-black/80 mb-2">
              Your cart is empty
            </h3>
            <p className="font-lexend text-sm text-black/50 mb-6">
              Add items to get started
            </p>
            <Button
              onClick={() => {
                closeCart();
                navigate('/shop');
              }}
              className="bg-primary-green hover:bg-primary-green/90"
            >
              Continue Shopping
            </Button>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1 min-h-0">
              <div className="px-6 py-4">
                <div className="flex flex-col gap-6">
                  {sellerGroups.map((group, groupIndex) => (
                    <div key={group.sellerId} className="space-y-3">
                      {/* Seller Header */}
                      <div className="flex items-center gap-2 pb-2">
                        <Store className="h-4 w-4 text-primary-green" />
                        <div className="flex-1">
                          <h3 className="font-lexend font-medium text-sm text-black">
                            {group.sellerName}
                          </h3>
                          <p className="font-lexend text-xs text-black/50">
                            {group.items.length} {group.items.length === 1 ? 'item' : 'items'}
                          </p>
                        </div>
                        <span className="font-lexend text-sm font-semibold text-black">
                          {formatPrice(group.subtotal)}
                        </span>
                      </div>

                      {/* Items in this seller group */}
                      <div className="space-y-3">
                        {group.items.map((item) => (
                          <CartItemCard 
                            key={item.id} 
                            item={item}
                            onQuantityChange={handleQuantityChange}
                            onEditVariants={handleEditVariants}
                            onRemove={removeFromCart}  
                          />
                        ))}
                      </div>

                      {/* Checkout Button for this seller */}
                      <Button
                        onClick={() => handleCheckout(group.sellerId)}
                        className="w-full h-10 bg-primary-green hover:bg-primary-green/90 rounded-[10px] font-lexend text-sm font-medium tracking-[-0.22px] capitalize text-[#F4FFF8]"
                      >
                        {user ? `Checkout ${group.sellerName}` : 'Login to Checkout'}
                      </Button>

                      {/* Separator between seller groups */}
                      {groupIndex < sellerGroups.length - 1 && (
                        <Separator className="my-4" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </ScrollArea>

            {/* Cart Summary Footer */}
            <div className="border-t border-black/5 flex-shrink-0">
              <div className="px-6 py-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-lexend text-sm font-normal text-black/60">
                    Total ({totalItems} {totalItems === 1 ? 'item' : 'items'})
                  </span>
                  <span className="font-lexend text-lg font-semibold text-black">
                    {formatPrice(totalPrice)}
                  </span>
                </div>
                <Separator />
                <p className="font-lexend text-xs text-black/50 text-center">
                  Items from different sellers must be checked out separately
                </p>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}